<template>
  <section class="min-h-screen grid lg:grid-cols-[55%_45%] bg-white">
    <!-- LEFT -->
    <div
      class="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500"
    >
      <div
        class="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10"
      ></div>

      <div
        class="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-white/10"
      ></div>

      <div class="relative z-10 flex flex-col h-full px-16 py-14 text-white">
        <img src="@/assets/images/logo.png" class="w-20" />

        <div class="mt-20">
          <p class="uppercase tracking-[0.35em] text-orange-100 text-sm">
            Secure Payment
          </p>

          <h1 class="mt-5 text-5xl font-bold leading-tight">
            IWC Payment<br />
            Center
          </h1>

          <p class="mt-8 max-w-lg text-lg leading-8 text-orange-100">
            Make secure healthcare payments online. Every transaction is
            encrypted and protected using industry-standard security.
          </p>
        </div>

        <div v-if="false" class="mt-12 space-y-5">
          <div class="flex items-center gap-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
            >
              🔒
            </div>

            <div>
              <p class="font-semibold">Secure Verification</p>
              <p class="text-sm text-orange-100">
                256-bit encrypted verification
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
            >
              ⚡
            </div>

            <div>
              <p class="font-semibold">Fast Processing</p>
              <p class="text-sm text-orange-100">
                Complete payment within minutes
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
            >
              🛡️
            </div>

            <div>
              <p class="font-semibold">Trusted Protection</p>
              <p class="text-sm text-orange-100">
                Your information stays private
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RIGHT -->
    <!-- RIGHT SIDE -->
    <div
      class="flex min-h-screen items-center justify-center px-4 py-8 lg:min-h-0 lg:px-10"
    >
      <div class="w-full max-w-md">
        <!-- Mobile Logo -->
        <div class="mb-6 flex justify-center lg:hidden">
          <img src="@/assets/images/logo.png" class="h-14" />
        </div>

        <!-- Title -->
        <div class="mt-8 text-center">
          <h1 class="text-4xl font-bold text-zinc-900">Verify your identity</h1>

          <p class="mt-3 text-base leading-7 text-zinc-500">
            We've sent a 4-digit verification code to your registered mobile
            number. Enter the code below to continue.
          </p>
        </div>

        <!-- Member -->
        <div class="mt-8 p-5 text-center">
          <p class="text-xs uppercase tracking-[0.3em] text-orange-500">
            Member
          </p>

          <h2 class="mt-2 text-2xl font-bold text-zinc-900">
            {{ getMemberName }}
          </h2>
        </div>

        <!-- OTP Form -->
        <form
          class="mt-8 flex flex-col items-center"
          @submit.prevent="verifyHandler()"
        >
          <!-- Alert Badge -->
          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div
              v-if="alertMessage"
              :class="[
                'mb-5 flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium',
                alertType === 'success'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : alertType === 'warning'
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : 'border-red-200 bg-red-50 text-red-700',
              ]"
            >
              <!-- Success -->
              <svg
                v-if="alertType === 'success'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>

              <!-- Warning -->
              <svg
                v-else-if="alertType === 'warning'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01M10.29 3.86l-8.08 14A2 2 0 004 21h16a2 2 0 001.79-3.14l-8.08-14a2 2 0 00-3.42 0z"
                />
              </svg>

              <!-- Error -->
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>

              <span>{{ alertMessage }}</span>
            </div>
          </Transition>

          <label class="mb-4 text-sm font-semibold text-zinc-700">
            Verification Code
          </label>

          <div class="flex justify-center gap-2">
            <input
              v-for="(_, index) in otp"
              :key="index"
              :ref="(el) => (otpInputs[index] = el)"
              v-model="otp[index]"
              maxlength="1"
              inputmode="numeric"
              pattern="[0-9]*"
              @input="handleInput(index)"
              @keydown.backspace="handleBackspace(index)"
              class="h-14 w-14 rounded-xl border border-zinc-300 bg-zinc-50 text-center text-2xl font-bold outline-none transition-all focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            class="mt-6 flex h-12 w-full items-center justify-center rounded-xl bg-orange-600 font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-orange-700 disabled:opacity-60"
          >
            <span v-if="!isLoading">Submit</span>

            <span v-else class="flex items-center gap-2">
              <LoadingSVG />
            </span>
          </button>
        </form>

        <!-- Divider -->
        <div class="my-8 h-px bg-zinc-200"></div>

        <!-- Security -->
        <div class="flex items-start gap-4">
          <div>
            <h3 class="font-semibold text-zinc-900">Powered by Xendit</h3>

            <p class="mt-2 text-sm leading-6 text-zinc-500">
              Payments are securely processed through Xendit, a trusted payment
              gateway providing reliable and secure online payment services.
            </p>
          </div>
        </div>

        <!-- Version -->
        <div class="mt-10 flex justify-center">
          <SemanticVersion />
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { inject, onMounted, reactive, ref, toRefs } from "vue";
import { useRoute, useRouter } from "vue-router";

export default {
  name: "AuthView",

  setup() {
    const route = useRoute();
    const router = useRouter();

    const axios = inject("$axios");

    const isLoading = ref(false);
    const getMemberName = ref("");

    const otp = ref(["", "", "", ""]);
    const otpInputs = ref([]);

    const formData = reactive({
      birthdate: null,
    });

    const alertMessage = ref("");
    const alertType = ref("error");

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const renderUserCard = async () => {
      try {
        const { data } = await axios.get(
          `/meta/api/amap/user/${route.params.id}`
        );

        getMemberName.value = data.data.membername;
      } catch (err) {
        console.log(err);
      }
    };

    onMounted(() => {
      renderUserCard();
    });

    const handleInput = (index) => {
      otp.value[index] = otp.value[index].replace(/\D/g, "");

      if (otp.value[index] && index < 3) {
        otpInputs.value[index + 1]?.focus();
      }
    };

    const handleBackspace = (index) => {
      if (!otp.value[index] && index > 0) {
        otpInputs.value[index - 1]?.focus();
      }
    };

    const verifyHandler = async () => {
      alertMessage.value = "";

      const otpCode = otp.value.join("");
      console.log("Otp", otpCode);

      try {
        isLoading.value = true;

        if (otpCode.length !== 4) {
          await delay(3000);
          alertType.value = "warning";
          alertMessage.value =
            "Please enter the complete 4-digit verification code.";
          isLoading.value = false;
          return;
        }

        if (otpCode === "1403") {
          await delay(3000);
          router.push("/payment/center/home");
          isLoading.value = false;
          return;
        }

        if (otpCode !== "1403") {
          await delay(3000);
          otp.value = ["", "", "", ""];

          alertType.value = "error";
          alertMessage.value = "Invalid verification code. Please try again.";
          isLoading.value = false;
          otpInputs.value[0]?.focus();
          return;
        }
      } catch (error) {
        await delay(3000);
      }
    };

    return {
      ...toRefs(formData),
      getMemberName,
      isLoading,
      otp,
      otpInputs,
      alertMessage,
      alertType,
      handleInput,
      handleBackspace,
      verifyHandler,
    };
  },
};
</script>
